
import { ReactNode, useEffect, useState } from 'react';
import { useAuth } from './AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { PRODUCTION_CONFIG } from '@/config/production';

interface ProtectedRouteProps {
  children: ReactNode;
  requireAdmin?: boolean;
}

export const ProtectedRoute = ({ children, requireAdmin = false }: ProtectedRouteProps) => {
  const { user, loading, isAuthenticated } = useAuth();
  const [isCheckingAdmin, setIsCheckingAdmin] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminCheckComplete, setAdminCheckComplete] = useState(false);
  
  // Ban check state
  const [isBanned, setIsBanned] = useState(false);
  const [banCheckComplete, setBanCheckComplete] = useState(false);
  
  if (PRODUCTION_CONFIG.monitoring.enableConsoleLogging) {
    console.log("ProtectedRoute - User:", user?.email, "Loading:", loading, "Authenticated:", isAuthenticated, "RequireAdmin:", requireAdmin);
  }

  // Check ban status for all authenticated users
  useEffect(() => {
    const checkBanStatus = async () => {
      if (user && isAuthenticated && !banCheckComplete) {
        try {
          const { data, error } = await supabase.rpc('is_banned', {
            check_user_id: user.id
          });
          
          if (error) {
            console.error('Error checking ban status:', error);
            setIsBanned(false);
          } else {
            if (PRODUCTION_CONFIG.monitoring.enableConsoleLogging) {
              console.log('Ban check result for', user.email, ':', data);
            }
            setIsBanned(!!data);
          }
        } catch (error) {
          console.error('Ban check failed:', error);
          setIsBanned(false);
        } finally {
          setBanCheckComplete(true);
        }
      }
    };

    if (!loading && user) {
      checkBanStatus();
    }
  }, [user, isAuthenticated, loading, banCheckComplete]);

  // Check admin status if required
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (requireAdmin && user && isAuthenticated && !adminCheckComplete) {
        if (PRODUCTION_CONFIG.monitoring.enableConsoleLogging) {
          console.log("ProtectedRoute - Checking admin status for:", user.email);
        }
        setIsCheckingAdmin(true);
        
        try {
          // Use the admin check function that verifies against the admin_users table
          const { data, error } = await supabase.rpc('check_admin_status', {
            user_email: user.email
          });
          
          if (error) {
            console.error('Error checking admin status:', error);
            setIsAdmin(false);
          } else {
            if (PRODUCTION_CONFIG.monitoring.enableConsoleLogging) {
              console.log('Admin check result for', user.email, ':', data);
            }
            setIsAdmin(!!data);
          }
        } catch (error) {
          console.error('Admin verification failed:', error);
          setIsAdmin(false);
        } finally {
          setIsCheckingAdmin(false);
          setAdminCheckComplete(true);
        }
      } else if (!requireAdmin) {
        // If admin not required, mark check as complete
        setAdminCheckComplete(true);
      }
    };

    checkAdminStatus();
  }, [requireAdmin, user, isAuthenticated, adminCheckComplete]);

  // Show loading state while auth is initializing
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh]">
        <Loader2 className="h-12 w-12 text-win95-blue animate-spin" />
        <p className="mt-4 text-win95-blue font-medium">Loading authentication...</p>
      </div>
    );
  }

  // Redirect to auth if not authenticated
  if (!user) {
    if (PRODUCTION_CONFIG.monitoring.enableConsoleLogging) {
      console.log("ProtectedRoute: No user after loading=false; redirecting to /auth");
    }
    return <Navigate to="/auth" replace />;
  }

  // Show loading while checking ban status
  if (!banCheckComplete) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh]">
        <Loader2 className="h-12 w-12 text-win95-blue animate-spin" />
        <p className="mt-4 text-win95-blue font-medium">Checking account status...</p>
      </div>
    );
  }

  // Redirect banned users to suspension page
  if (isBanned) {
    if (PRODUCTION_CONFIG.monitoring.enableConsoleLogging) {
      console.log("User", user.email, "is banned, redirecting to /suspended");
    }
    return <Navigate to="/suspended" replace />;
  }

  // Show loading while checking admin status
  if (requireAdmin && isCheckingAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh]">
        <Loader2 className="h-12 w-12 text-win95-blue animate-spin" />
        <p className="mt-4 text-win95-blue font-medium">Verifying admin permissions...</p>
      </div>
    );
  }

  // Check admin access if required and check is complete
  if (requireAdmin && adminCheckComplete && !isAdmin) {
    if (PRODUCTION_CONFIG.monitoring.enableConsoleLogging) {
      console.log("Admin access required but user", user.email, "is not in admin_users table");
    }
    return <Navigate to="/dashboard" replace />;
  }

  // Don't render children until admin check is complete (if required)
  if (requireAdmin && !adminCheckComplete) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh]">
        <Loader2 className="h-12 w-12 text-win95-blue animate-spin" />
        <p className="mt-4 text-win95-blue font-medium">Checking permissions...</p>
      </div>
    );
  }

  // Render children if user is authenticated (and admin if required)
  if (PRODUCTION_CONFIG.monitoring.enableConsoleLogging) {
    console.log("User", user.email, "authenticated, admin status:", isAdmin, "rendering children");
  }
  return <>{children}</>;
};

export default ProtectedRoute;
